import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ChevronLeft, Globe } from "lucide-react";
import ShareButton from "@/components/ShareButton";

const BTN = "px-4 py-2 rounded-full border-[3px] border-[#374151] font-black text-sm shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none";
const INPUT = "w-full px-3 py-2 rounded-lg border-[2px] border-[#374151] font-bold bg-white";
const blankQ = (type = "mcq") => ({ type, text: "", options: type === "tf" ? ["True", "False"] : ["", "", "", ""], correct_index: 0 });
const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.[0]?.msg?.replace("Value error, ", "") || "Failed"; };

function ClassroomChecks({ rooms, value, onChange }) {
  if (rooms.length === 0) return <p className="text-sm text-slate-500">Create a classroom first to share this quiz with students.</p>;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="flex flex-wrap gap-3">
      {rooms.map((c) => (
        <label key={c.id} className="font-bold text-sm flex items-center gap-2">
          <input type="checkbox" checked={value.includes(c.id)} onChange={() => toggle(c.id)} className="w-5 h-5" data-testid={`tq-classroom-${c.id}`} />
          {c.name} <span className="text-slate-400">({c.status === "approved" ? c.code : "pending"})</span>
        </label>
      ))}
    </div>
  );
}

function QuestionForm({ initial, onSave, onCancel }) {
  const [q, setQ] = useState(initial);
  const setType = (type) => setQ({ ...blankQ(type), text: q.text });
  return (
    <div className="sticker-card p-5">
      <div className="flex gap-2 mb-3">
        {["mcq", "tf"].map((t) => (
          <button key={t} onClick={() => setType(t)} data-testid={`tq-type-${t}`}
            className={`${BTN} ${q.type === t ? "bg-[var(--tp-accent)]" : "bg-white"}`}>{t === "mcq" ? "Multiple choice" : "True / False"}</button>
        ))}
      </div>
      <input value={q.text} onChange={(e) => setQ({ ...q, text: e.target.value })} placeholder="Question" className={INPUT} data-testid="tq-question-text" />
      <div className="mt-3 space-y-2">
        {q.options.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="radio" checked={q.correct_index === i} onChange={() => setQ({ ...q, correct_index: i })} className="w-5 h-5" data-testid={`tq-correct-${i}`} />
            {q.type === "mcq" ? (
              <input value={o} onChange={(e) => setQ({ ...q, options: q.options.map((x, j) => (j === i ? e.target.value : x)) })}
                placeholder={`Option ${i + 1}`} className={INPUT} data-testid={`tq-option-${i}`} />
            ) : <span className="font-black">{o}</span>}
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-2">Tap the circle next to the right answer.</p>
      <div className="mt-4 flex gap-2">
        <button onClick={() => onSave(q)} data-testid="tq-save-question" className={`${BTN} bg-[var(--tp-primary)] text-white`}>{initial.id ? "Save changes" : "Add question"}</button>
        {onCancel && <button onClick={onCancel} className={`${BTN} bg-white`}>Cancel</button>}
      </div>
    </div>
  );
}

function QuizEditor({ api, id, rooms, cats, onBack }) {
  const [quiz, setQuiz] = useState(null);
  const [meta, setMeta] = useState({ title: "", description: "", classroom_ids: [] });
  const [editing, setEditing] = useState(null);
  const [pub, setPub] = useState({ category_id: "", note: "" });
  const load = useCallback(async () => {
    const r = await api.get(`/teacher/quizzes/${id}`);
    setQuiz(r.data);
    const list = await api.get("/teacher/quizzes");
    const mine = list.data.find((x) => x.id === id);
    setMeta({ title: r.data.title, description: r.data.description, classroom_ids: mine?.classroom_ids || [] });
  }, [api, id]);
  useEffect(() => { load(); }, [load]);
  if (!quiz) return <p>Loading…</p>;

  const saveMeta = async () => { try { await api.patch(`/teacher/quizzes/${id}`, meta); toast.success(quiz.visibility === "public" ? "Classrooms saved" : "Quiz saved"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const saveQ = async (q) => {
    try {
      if (q.id) await api.patch(`/teacher/quizzes/${id}/questions/${q.id}`, q);
      else await api.post(`/teacher/quizzes/${id}/questions`, q);
      setEditing(null); toast.success("Saved"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delQ = async (qid) => { try { await api.delete(`/teacher/quizzes/${id}/questions/${qid}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const requestPublic = async () => {
    try { await api.post(`/teacher/quizzes/${id}/request-public`, pub); toast.success("Sent to admin for review"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const status = quiz.visibility === "public" ? "public" : quiz.public_request?.status;
  const isPublic = status === "public";

  return (
    <div data-testid="teacher-quiz-editor">
      <button onClick={onBack} className="inline-flex items-center gap-1 font-bold mb-4 hover:underline" data-testid="tq-back"><ChevronLeft size={18} /> All my quizzes</button>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          <div className="sticker-card p-5 space-y-3">
            <input value={meta.title} disabled={isPublic} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className={`${INPUT} disabled:opacity-60`} data-testid="tq-edit-title" />
            <input value={meta.description} disabled={isPublic} onChange={(e) => setMeta({ ...meta, description: e.target.value })} placeholder="Description" className={`${INPUT} disabled:opacity-60`} data-testid="tq-edit-description" />
            <p className="font-black text-sm">Share with classrooms</p>
            <ClassroomChecks rooms={rooms} value={meta.classroom_ids} onChange={(v) => setMeta({ ...meta, classroom_ids: v })} />
            <div className="flex gap-2 flex-wrap">
              <button onClick={saveMeta} className={`${BTN} bg-[var(--tp-success)] text-white`} data-testid="tq-save-meta">Save</button>
              <ShareButton path={`/quiz/${id}`} label="Copy quiz link" testid="tq-share" title={quiz.title} />
            </div>
          </div>

          <div className="sticker-card p-5" data-testid="tq-public-box">
            <p className="font-black flex items-center gap-2"><Globe size={18} strokeWidth={2.5} /> Make it public for everyone</p>
            {status === "public" && <p className="mt-2 font-bold text-[var(--tp-success)]" data-testid="tq-public-status">This quiz is public in the main library.</p>}
            {status === "pending" && <p className="mt-2 font-bold" data-testid="tq-public-status">Waiting for admin review.</p>}
            {(!status || status === "rejected") && (
              <div className="mt-3 space-y-2">
                {status === "rejected" && <p className="text-sm font-bold text-[#FF4B4B]" data-testid="tq-public-status">The admin didn't approve the last request. You can try again.</p>}
                <select value={pub.category_id} onChange={(e) => setPub({ ...pub, category_id: e.target.value })} className={INPUT} data-testid="tq-public-category">
                  <option value="">Suggested category (optional)</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input value={pub.note} onChange={(e) => setPub({ ...pub, note: e.target.value })} placeholder="Note to admin (optional)" className={INPUT} />
                <button onClick={requestPublic} className={`${BTN} bg-white`} data-testid="tq-request-public">Ask admin to publish</button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {status === "public" ? (
            <p className="sticker p-4 font-bold text-slate-600" data-testid="tq-locked">This quiz is in the public library, so the admin now manages its questions.</p>
          ) : (
            <QuestionForm key={editing?.id || `new-${quiz.questions.length}`} initial={editing || blankQ()} onSave={saveQ} onCancel={editing ? () => setEditing(null) : null} />
          )}
          <div className="sticker" data-testid="tq-question-list">
            {quiz.questions.length === 0 && <p className="p-4 text-slate-500">No questions yet.</p>}
            {quiz.questions.map((q, i) => (
              <div key={q.id} className="p-4 border-b-[2px] border-slate-200 last:border-0 flex items-start gap-3">
                <div className="flex-1">
                  <p className="font-black">{i + 1}. {q.text}</p>
                  <p className="text-sm text-slate-500">{q.type === "mcq" ? "Multiple choice" : "True/False"} • answer: {q.options[q.correct_index]}</p>
                </div>
                {!isPublic && (<>
                  <button onClick={() => setEditing(q)} data-testid={`tq-edit-q-${i}`}><Pencil size={16} /></button>
                  <button onClick={() => delQ(q.id)} className="text-[#FF4B4B]" data-testid={`tq-delete-q-${i}`}><Trash2 size={16} /></button>
                </>)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TeacherQuizzes({ api }) {
  const [list, setList] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", classroom_ids: [] });
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => api.get("/teacher/quizzes").then((r) => setList(r.data)), [api]);
  useEffect(() => {
    load();
    api.get("/classrooms/teaching").then((r) => setRooms(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, [load, api]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Give your quiz a title");
    try { const r = await api.post("/teacher/quizzes", form); setForm({ title: "", description: "", classroom_ids: [] }); setOpenId(r.data.id); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this quiz and its questions?")) return; await api.delete(`/teacher/quizzes/${id}`); load(); };

  if (openId) return <QuizEditor api={api} id={openId} rooms={rooms} cats={cats} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <div>
      <div className="sticker-card p-6" style={{ background: "#EBFBD6" }}>
        <h2 className="font-black text-lg mb-3">Build a class quiz</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Chapter 4 vocab" className={INPUT} data-testid="tq-new-title" />
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" className={INPUT} data-testid="tq-new-description" />
        </div>
        <div className="mt-3"><ClassroomChecks rooms={rooms} value={form.classroom_ids} onChange={(v) => setForm({ ...form, classroom_ids: v })} /></div>
        <button onClick={create} className={`${BTN} mt-4 bg-[var(--tp-success)] text-white inline-flex items-center gap-1`} data-testid="tq-create">
          <Plus size={16} strokeWidth={2.5} /> Create & add questions
        </button>
        <p className="text-sm text-slate-600 mt-2">Free for verified teachers. Only students in the classrooms you pick can play it.</p>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="tq-list">
        {list.length === 0 && <p className="text-slate-500">No class quizzes yet.</p>}
        {list.map((q) => (
          <div key={q.id} className="sticker-card p-5">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xl font-black truncate">{q.title}</h3>
              <span className="px-3 py-1 rounded-full border-[2px] border-[#374151] font-black text-xs uppercase bg-white">
                {q.visibility === "public" ? "public" : q.public_request?.status === "pending" ? "review" : "class only"}
              </span>
            </div>
            <p className="text-sm text-slate-600 mt-1">{q.question_count} question(s) • {q.classroom_ids.length} classroom(s)</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setOpenId(q.id)} className={`${BTN} bg-[var(--tp-primary)] text-white`} data-testid={`tq-open-${q.id}`}>Edit</button>
              {q.visibility !== "public" && <button onClick={() => del(q.id)} className="p-2 text-[#FF4B4B]" data-testid={`tq-delete-${q.id}`}><Trash2 size={16} /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
